<template>
  <div class="position-card-form">
    <ExBreadcrumbs 
      class="breadcrumbs-area-form" 
      :breadcrumbsList="computedBreadcrumbs"
      @onClickBreadcrumb="handleBreadcrumbClick"
    />
    <div class="position-header-form">
      <OrgObjectHeader
        type="Position"
        :status="positionData?.status || status"
        :code="positionData ? getOrgCodeName(positionData) : code"
        :name="positionData?.name || name"
        :archived="positionData?.archived ?? archived"
        :currentPosition="positionData"
        :modalHeaderState="positionHeaderState"
        :dataStream="positionChatterData"
        :totalChatterCount="totalPositionChatterData"
        :chatterDrawerOpen="chatterDrawerOpen"
        @update:chatterDrawerOpen="chatterDrawerOpen = $event"
        @reloadChatterList="reloadChatterData"
        @requestMoreCheckpointsChatter="getChatterData"
        @optionClick="handleOptionClick"
      />
    </div>
    <PositionCardMenu
      :value="selectedTab"
      :attached-roles-count="attachedRolesCount"
      :assignments-count="currentAssignmentsCount"
      @tab-change="onTabChange"
    />
    
    <div class="tabs-pane">
      <div v-if="selectedTab === 0" class="tab-content tab-purpose">
        <PurposeViewEdit
          :value="positionData?.purpose || purpose"
          placeholder="Enter purpose for this position..."
          :maxLength="500"
          :isSaving="isSavingPurpose"
          @save="onPurposeSave"
          :hideAIButtons="true"
        />
      </div>
      <div v-if="selectedTab === 1" class="tab-content tab-attached-roles">
        <PositionAttachedRolesTab 
          ref="attachedRolesTabRef"
          :key="positionData?.id || positionId"
          :positionId="positionData?.id || positionId"
          :positionName="positionData?.name || name"
          @drilldown="handleDrilldown"
          @refresh-position-data="handleRefreshPositionData"
        />
      </div>
      <div v-if="selectedTab === 2" class="tab-content tab-assignments">
      </div>
    </div>
    
    <!-- Dialogs -->
    <RenameOrgObject
      v-if="showRenamePositionDialog"
      :openDialog="showRenamePositionDialog"
      :name="positionData?.name || name"
      OrgObjectType="Position"
      :placeholder="positionData?.name || name"
      @onAction="renamePosition($event)"
      @onClose="showRenamePositionDialog = false"
      :isShowCharCounter="true"
      :maxWidth="'430'"
    />
    
    <ExCloneOrgObject
      v-model="showClonePositionDialog"
      OrgObjectType="Position"
      :name="positionData?.name || name"
      @onAction="clonePosition"
    />
    
    <AttachedRole
      v-model="showAttachRoleDialog"
      OrgObjectType="Role to Position"
      @onSave="handleAttachRole"
      :items="attachableRoles"
      @item-selected="handleRoleSelected"
      :existingNamesList="attachedRoleNames"
      @add-item-selected="handleAddNewRole"
      :parent-name="positionData?.name || name"
      :openDefault="openDefault"
      :type="'attach_role_to_position_new_picker'"
      @load-more="loadMoreRoles"
      @search="searchRoles"
      :hasMoreData="hasMoreRolesData"
      :isLoadingMore="isLoadingMoreRoles"
      :isAssigning="isAssigningRole"
    />
    
    <RetirePositionPopup
      v-model="showRetirePositionDialog"
      OrgObjectType="Position"
      :name="positionData?.name || name"
      @onAction="retirePositionAction"
    />
    
    <ReActivatePositionPopup
      v-model="showReactivatePositionDialog"
      OrgObjectType="Position"
      :name="positionData?.name || name"
      @onAction="reactivatePositionAction"
    />
    
    <ArchivePopUp
      :show="showArchiveDialog"
      type="position"
      :curretObj="positionData || { name: name }"
      :action="archiveAction"
      @onArchive="handleArchivePosition"
      @onClose="showArchiveDialog = false"
    />
    
    <ExOrgObjectActions
      v-model="showActivatePositionDialog"
      objectType="Position"
      :name="positionData?.name || name"
      @onAction="activatePositionAction"
    />
    
    <ExDeleteOrgObject
      v-model="showDeletePositionDialog"
      OrgObjectType="Position"
      :name="positionData?.name || name"
      @onAction="deletePositionAction"
    />
  </div>
</template>

<script setup>
import { defineProps, ref, computed, onMounted, watch, getCurrentInstance } from 'vue';
import ExBreadcrumbs from '@components/breadcrumbs/ExBreadcrumbs.vue';
import OrgObjectHeader from '@components/OrgObjectHeader/OrgObjectHeader.vue';
import PositionCardMenu from '@components/PositionCardMenu/PositionCardMenu.vue';
import PurposeViewEdit from '@components/PurposeViewEdit/PurposeViewEdit.vue';
import PositionAttachedRolesTab from '@components/PositionAttachedRolesTab/PositionAttachedRolesTab.vue';
import RenameOrgObject from '@components/RenameOrgObject/RenameOrgObject.vue';
import ExCloneOrgObject from '@components/CloneOrgObject/ExCloneOrgObject.vue';
import AttachedRole from '@components/RolesCatalog/AttachedRole.vue';
import RetirePositionPopup from '@components/RetirePositionPopup/RetirePositionPopup.vue';
import ReActivatePositionPopup from '@components/ReActivatePosition/ReActivatePositionPopup.vue';
import ArchivePopUp from '@components/common/ArchivePopUp.vue';
import ExOrgObjectActions from '@components/common/ExOrgObjectActions.vue';
import ExDeleteOrgObject from '@components/DeleteOrgObject/ExDeleteOrgObject.vue';
import { 
  getPositionId, 
  getChatterList, 
  updatePositionExcludeValue,
  updatePosition,
  createPosition,
  attachRoleToPosition,
  fetchAttachableRolesForPosition,
  createRole,
  retirePosition,
  reActivatePosition,
  archivePosition,
  unArchivePosition,
  activatePosition,
  deletePosition
} from '@/api';

const props = defineProps({
  breadcrumbsList: {
    type: Array,
    default: () => []
  },
  status: {
    type: String,
    default: ''
  },
  code: {
    type: String,
    default: ''
  },
  name: {
    type: String,
    default: ''
  },
  currentAssignmentsCount: {
    type: Number,
    default: 0
  },
  purpose: {
    type: String,
    default: ''
  },
  positionId: {
    type: [Number, String],
    default: null
  },
  archived: {
    type: Boolean,
    default: false
  }
});

const instance = getCurrentInstance();
const proxy = instance?.proxy;
const route = proxy?.$route;

const emit = defineEmits(['backToList', 'drilldown']);

const selectedTab = ref(0);
const positionData = ref(null);
const accountId = ref(null);
const positionId = ref(null);
const positionChatterData = ref([]);
const totalPositionChatterData = ref(0);
const loadedPages = ref({});
const isSavingPurpose = ref(false);
const chatterDrawerOpen = ref(false);

// Dialog states
const showRenamePositionDialog = ref(false);
const showClonePositionDialog = ref(false);
const showAttachRoleDialog = ref(false);
const showRetirePositionDialog = ref(false);
const showReactivatePositionDialog = ref(false);
const showArchiveDialog = ref(false);
const archiveAction = ref('archive'); // 'archive' or 'unarchive'
const showActivatePositionDialog = ref(false);
const showDeletePositionDialog = ref(false);

// Attach role related states
const attachableRoles = ref([]);
const selectedRoleId = ref(null);
const openDefault = ref(false);
const hasMoreRolesData = ref(false);
const isLoadingMoreRoles = ref(false);
const isAssigningRole = ref(false);
const rolesPage = ref(1);
const rolesSearchTerm = ref('');
const rolesLimit = 10;
const attachedRolesCount = ref(0);
const attachedRolesTabRef = ref(null);

function onTabChange(tabIndex) {
  selectedTab.value = tabIndex;
  if (proxy.$router && route) {
    const positionTabMap = ['purpose', 'attached-roles', 'assignments'];
    const positionTabValue = positionTabMap[tabIndex] || 'purpose';
    
    proxy.$router.replace({
      ...route,
      query: {
        ...route.query,
        positionTab: positionTabValue
      }
    }).catch(() => {
    });
  }
}

function handleBreadcrumbClick(breadcrumb) {
  if (breadcrumb.text === 'Positions') {
    if (proxy?.$router) {
      proxy.$router.push('/new-organization/positions').catch(() => {});
    }
  }
}

function handleDrilldown(data) {
  emit('drilldown', data);
}

async function handleRefreshPositionData() {
  const idPosition = positionData.value?.id || positionId.value;
  if (idPosition) {
    await getPositionById(idPosition);
    // Update the count from the refreshed position data
    attachedRolesCount.value = positionData.value?.roles_count ?? 0;
  }
}

async function refreshAttachedRolesTab() {
  if (attachedRolesTabRef.value && typeof attachedRolesTabRef.value.refreshRoles === 'function') {
    await attachedRolesTabRef.value.refreshRoles();
  }
}

const attachedRoleNames = computed(() => {
  return []; 
});

const computedPositionId = computed(() => {
  return props.positionId || route?.params?.positionId || route?.query?.positionId || null;
});

const computedBreadcrumbs = computed(() => {
  if (props.breadcrumbsList && props.breadcrumbsList.length > 0) {
    return props.breadcrumbsList;
  }
  
  return [
    {
      text: "Positions",
      disabled: false,
      href: "/new-organization/positions",
    },
    {
      text: "Position",
      disabled: true,
    },
  ];
});

const positionHeaderState = computed(() => {
  return {
    status: positionData.value?.status || props.status,
    icon: 'org_icon_position',
    label: 'Position',
    code: positionData.value ? getOrgCodeName(positionData.value) : props.code,
    name: positionData.value?.name || props.name,
    archived: positionData.value?.archived ?? props.archived,
    assigned_employees: positionData.value?.assigned_employees || []
  };
});

function getOrgCodeName(currentPosition) {
  if (currentPosition?.code_str) {
    return currentPosition.code_str.split(" ")[0];
  } else {
    return "#" + currentPosition?.code;
  }
}

async function onPurposeSave(value) {
  try {
    isSavingPurpose.value = true;
    const idPosition = positionData.value?.id;
    if (!idPosition) {
      throw new Error('No position ID');
    }
    const payload = {
      purpose: value,
    };
    await updatePositionExcludeValue(accountId.value, idPosition, payload);
    await getPositionById(idPosition);
  } catch (error) {
    console.error('Error updating purpose:', error);
  } finally {
    isSavingPurpose.value = false;
  }
}

async function getPositionById(idPosition) {
  try {
    const { data } = await getPositionId(accountId.value, idPosition);
    positionData.value = data;
  } catch (error) {
    console.error('Error fetching position:', error);
  }
}

async function fetchPositionData() {
  try {
    if (!accountId.value || !positionId.value) {
      console.warn('Missing accountId or positionId:', { accountId: accountId.value, positionId: positionId.value });
      return;
    }
    
    positionData.value = null;
    
    const { data } = await getPositionId(accountId.value, positionId.value);
    
    if (data) {
      positionData.value = data;
    } else {
      console.warn('No position data found for ID:', positionId.value);
      positionData.value = null;
    }
  } catch (error) {
    console.error('Error fetching position data:', error);
    console.error('Error details:', error.response?.data || error.message);
    positionData.value = null;
  }
}

async function reloadChatterData() {
  if (!positionData.value) return;
  
  const currentPositionId = positionData.value.id;
  
  positionChatterData.value = [];
  totalPositionChatterData.value = 0;
  if (loadedPages.value[currentPositionId]) {
    loadedPages.value[currentPositionId].clear();
  }
  
  await getChatterData();
}

async function getChatterData() {
  if (!positionData.value) return;
  
  const currentPositionId = positionData.value.id;
  const currentLength = positionChatterData.value.length;
  const page = Math.floor(currentLength / 10) + 1;
  
  if (!loadedPages.value[currentPositionId]) {
    loadedPages.value[currentPositionId] = new Set();
  }
  
  if (loadedPages.value[currentPositionId].has(page)) {
    return;
  }
  
  try {
    const { data } = await getChatterList(accountId.value, currentPositionId, "position", page);
    loadedPages.value[currentPositionId].add(page);
    
    if (data && data.count) {
      totalPositionChatterData.value = data.count;
    }
    
    if (data && data.results) {
      let chatterItems = data.results.map((el) => {
        const date = new Date(el.recorded_on);
        const milliseconds = date.getTime();
        const millisecondsString = milliseconds.toString();
        let nameParts = el.recorded_by_name.split(" ");
        const newEl = {
          id: el.id,
          IsExIQtiveBot: false,
          message: el.note,
          created_on: millisecondsString,
          IsHistory: el.is_history,
        };
        
        if (nameParts.length === 2) {
          newEl.avatar = {
            picture: "",
            firstName: nameParts[0],
            lastName: nameParts[1],
          };
        } else {
          newEl.avatar = {
            picture: "",
            firstName: el.recorded_by_name,
            lastName: "",
          };
        }
        
        return newEl;
      });
      positionChatterData.value.push(...chatterItems);
    }
  } catch (error) {
    console.error('Error fetching position chatter data:', error);
  }
}

function handleOptionClick(operation) {
  const action = operation.item?.value;
  
  switch (action) {
    case 'activate_action':
      showActivatePositionDialog.value = true;
      break;
    case 'retire_action':
      showRetirePositionDialog.value = true;
      break;
    case 'reactivate_action':
      showReactivatePositionDialog.value = true;
      break;
    case 'rename_action':
      showRenamePositionDialog.value = true;
      break;
    case 'delete_action':
      showDeletePositionDialog.value = true;
      break;
    case 'archive_action':
      archiveAction.value = 'archive';
      showArchiveDialog.value = true;
      break;
    case 'unarchive_action':
      archiveAction.value = 'unarchive';
      showArchiveDialog.value = true;
      break;
    case 'attach_position_role':
      handleOpenAttachRole();
      break;
    case 'cloned_action':
      showClonePositionDialog.value = true;
      break;
    default:
      break;
  }
}

async function handleOpenAttachRole() {
  showAttachRoleDialog.value = true;
  openDefault.value = true;
  rolesPage.value = 1;
  rolesSearchTerm.value = '';
  await fetchAttachableRolesList();
}

async function fetchAttachableRolesList(isLoadMore = false) {
  try {
    if (isLoadMore) {
      isLoadingMoreRoles.value = true;
    }
    
    const offset = (rolesPage.value - 1) * rolesLimit;
    const { data } = await fetchAttachableRolesForPosition(
      accountId.value,
      positionData.value?.id || positionId.value,
      rolesLimit,
      offset,
      rolesSearchTerm.value
    );
    
    if (isLoadMore) {
      const mappedResults = (data.results || []).map(item => {
        const code = item.code || item.role_code || item.code_str?.match(/\d+/)?.[0] || '';
        return {
          ...item,
          role_code: code,
          code: code,
          attached: item.attached_to_position || false,
          disabled: item.attached_to_position || false
        };
      });
      attachableRoles.value.push(...mappedResults);
    } else {
      attachableRoles.value = (data.results || []).map(item => {
        const code = item.code || item.role_code || item.code_str?.match(/\d+/)?.[0] || '';
        return {
          ...item,
          role_code: code,
          code: code,
          attached: item.attached_to_position || false,
          disabled: item.attached_to_position || false
        };
      });
    }
    
    hasMoreRolesData.value = !!data.next;
  } catch (error) {
    console.error('Error fetching attachable roles:', error);
    attachableRoles.value = [];
    hasMoreRolesData.value = false;
  } finally {
    if (isLoadMore) {
      isLoadingMoreRoles.value = false;
    }
  }
}

function handleRoleSelected(role) {
  if (!role || !role.id) return;
  selectedRoleId.value = role.id;
}

async function handleAddNewRole(roleData) {
  try {
    proxy.$loader?.setAppLoading(true);
    const newRole = await createRole(accountId.value, { 
      name: roleData.name, 
      account: accountId.value 
    });
    
    if (newRole && newRole.data && newRole.data.id) {
      selectedRoleId.value = newRole.data.id;
      await handleAttachRole();
    }
  } catch (error) {
    console.error('Error creating and attaching role:', error);
  } finally {
    proxy.$loader?.setAppLoading(false);
  }
}

async function handleAttachRole() {
  if (!selectedRoleId.value) return;
  
  try {
    isAssigningRole.value = true;
    proxy.$loader?.setAppLoading(true);
    
    const payload = {
      attachment_id: selectedRoleId.value,
    };
    
    await attachRoleToPosition(
      accountId.value, 
      positionData.value?.id || positionId.value, 
      payload
    );
    
    showAttachRoleDialog.value = false;
    selectedRoleId.value = null;
    rolesPage.value = 1;
    rolesSearchTerm.value = '';
    
    await getPositionById(positionData.value?.id || positionId.value);
    
    // Update the attached roles count
    attachedRolesCount.value = positionData.value?.roles_count ?? 0;
    
    // Refresh the attached roles tab if it exists
    await refreshAttachedRolesTab();
  } catch (error) {
    console.error('Error attaching role:', error);
  } finally {
    isAssigningRole.value = false;
    proxy.$loader?.setAppLoading(false);
  }
}

async function loadMoreRoles() {
  rolesPage.value += 1;
  await fetchAttachableRolesList(true);
}

async function searchRoles(searchTerm) {
  rolesSearchTerm.value = searchTerm;
  rolesPage.value = 1;
  await fetchAttachableRolesList(false);
}

async function renamePosition(newName) {
  try {
    proxy.$loader?.setAppLoading(true);
    const idPosition = positionData.value?.id || positionId.value;
    if (!idPosition) {
      throw new Error('No position ID');
    }
    const payload = {
      name: newName,
      account: accountId.value,
    };
    await updatePosition(accountId.value, idPosition, payload);
    showRenamePositionDialog.value = false;
    await getPositionById(idPosition);
    await refreshAttachedRolesTab();
  } catch (error) {
    console.error('Error renaming position:', error);
  } finally {
    proxy.$loader?.setAppLoading(false);
  }
}

async function clonePosition() {
  try {
    proxy.$loader?.setAppLoading(true);
    const currentPositionName = positionData.value?.name || name;
    const payload = {
      name: 'Clone of ' + currentPositionName,
      account: accountId.value,
    };
    const { data } = await createPosition(accountId.value, payload);
    
    showClonePositionDialog.value = false;
    
    console.log('Position cloned successfully:', data);
  } catch (error) {
    console.error('Error cloning position:', error);
  } finally {
    proxy.$loader?.setAppLoading(false);
  }
}

async function retirePositionAction() {
  try {
    proxy.$loader?.setAppLoading(true);
    const idPosition = positionData.value?.id || positionId.value;
    if (!idPosition) {
      throw new Error('No position ID');
    }
    await retirePosition(accountId.value, idPosition);
    showRetirePositionDialog.value = false;
    await getPositionById(idPosition);
    await refreshAttachedRolesTab();
  } catch (error) {
    console.error('Error retiring position:', error);
  } finally {
    proxy.$loader?.setAppLoading(false);
  }
}

async function reactivatePositionAction() {
  try {
    proxy.$loader?.setAppLoading(true);
    const idPosition = positionData.value?.id || positionId.value;
    if (!idPosition) {
      throw new Error('No position ID');
    }
    await reActivatePosition(accountId.value, idPosition);
    showReactivatePositionDialog.value = false;
    await getPositionById(idPosition);
    await refreshAttachedRolesTab();
  } catch (error) {
    console.error('Error reactivating position:', error);
  } finally {
    proxy.$loader?.setAppLoading(false);
  }
}

async function handleArchivePosition() {
  try {
    proxy.$loader?.setAppLoading(true);
    const idPosition = positionData.value?.id || positionId.value;
    if (!idPosition) {
      throw new Error('No position ID');
    }
    
    if (archiveAction.value === 'archive') {
      await archivePosition(accountId.value, 'position', idPosition);
    } else {
      await unArchivePosition(accountId.value, 'position', idPosition);
    }
    
    showArchiveDialog.value = false;
    await getPositionById(idPosition);
    await refreshAttachedRolesTab();
  } catch (error) {
    console.error(`Error ${archiveAction.value}ing position:`, error);
  } finally {
    proxy.$loader?.setAppLoading(false);
  }
}

async function activatePositionAction() {
  try {
    proxy.$loader?.setAppLoading(true);
    const idPosition = positionData.value?.id || positionId.value;
    if (!idPosition) {
      throw new Error('No position ID');
    }
    await activatePosition(accountId.value, idPosition);
    showActivatePositionDialog.value = false;
    await getPositionById(idPosition);
    await refreshAttachedRolesTab();
  } catch (error) {
    console.error('Error activating position:', error);
  } finally {
    proxy.$loader?.setAppLoading(false);
  }
}

async function deletePositionAction() {
  try {
    proxy.$loader?.setAppLoading(true);
    const idPosition = positionData.value?.id || positionId.value;
    if (!idPosition) {
      throw new Error('No position ID');
    }
    await deletePosition(accountId.value, idPosition);
    showDeletePositionDialog.value = false;
    
    // Navigate back to positions list after delete
    if (proxy?.$router) {
      proxy.$router.push('/new-organization/positions').catch(() => {});
    }
  } catch (error) {
    console.error('Error deleting position:', error);
  } finally {
    proxy.$loader?.setAppLoading(false);
  }
}

watch(computedPositionId, async (newPositionId, oldPositionId) => {
  if (newPositionId && newPositionId !== oldPositionId && accountId.value) {
    try {
      proxy.$loader?.show();
      positionId.value = newPositionId;
      
      positionData.value = null;
      positionChatterData.value = [];
      totalPositionChatterData.value = 0;
      selectedTab.value = 0;
      
      if (oldPositionId && loadedPages.value[oldPositionId]) {
        loadedPages.value[oldPositionId].clear();
      }
      
      await fetchPositionData();
    } catch (error) {
      console.error('Error fetching position data on change:', error);
    } finally {
      proxy.$loader?.hide();
    }
  }
}, { immediate: false });

watch(() => route?.query?.positionId, async (newPositionId, oldPositionId) => {
  if (newPositionId && newPositionId !== oldPositionId && accountId.value) {
    try {
      proxy.$loader?.show();
      positionId.value = newPositionId;
      
      positionData.value = null;
      positionChatterData.value = [];
      totalPositionChatterData.value = 0;
      selectedTab.value = 0;
      
      if (oldPositionId && loadedPages.value[oldPositionId]) {
        loadedPages.value[oldPositionId].clear();
      }
      
      await fetchPositionData();
    } catch (error) {
      console.error('Error fetching position data on route change:', error);
    } finally {
      proxy.$loader?.hide();
    }
  }
}, { immediate: false });

onMounted(async () => {
  try {
    proxy.$loader?.show();
    accountId.value = JSON.parse(localStorage.getItem("currentAccountID"));
    positionId.value = computedPositionId.value;
    
    const positionTabFromQuery = route?.query?.positionTab;
    if (positionTabFromQuery) {
      const positionTabMap = { 'purpose': 0, 'attached-roles': 1, 'assignments': 2 };
      const tabIndex = positionTabMap[positionTabFromQuery.toLowerCase()];
      if (tabIndex !== undefined) {
        selectedTab.value = tabIndex;
      }
    }
    
    await fetchPositionData();
    attachedRolesCount.value = positionData.value?.roles_count ?? 0;
  } finally {
    proxy.$loader?.hide();
  }
});
</script>

<style scoped>
.position-card-form {
  background: #fff;
  border-radius: 8px;
  box-shadow: 0px 1px 2px 0px #1018280F, 0px 1px 3px 0px #1018281A;
  border: 1px solid #EAECF0;
  width: 100%;
  padding:16px;
}

.position-header-form {
  padding: 10px 0px 0px 0px;
}
.breadcrumbs-area-form {
  padding: 10px 0px 0px 5px !important;
}

::v-deep .position-header-form .header-header-container {
  position: relative;
  left: -10px;
}

::v-deep .position-header-form .header-org-menu {
  right: 0px !important;
  margin-right: 25px !important;
}

::v-deep .position-header-form .header-org-menu .header-chatter-type-icon {
  padding: 2px;
  left: 8px;
}

::v-deep .position-header-form .header-chatter-icon {
  margin-right: 7px !important;
}

.tab-purpose, .tab-assignments {
  width: 100%;
  padding-block: 16px;
}

::v-deep .roles-table-wrapper {
  border: none !important;
  box-shadow: none !important;
}
</style>
